"use client";

import { ListingPreview } from "@/components/listings/listing-preview";
import { BrokerProfileCard } from "@/components/brokers/broker-profile-card";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { buildListingUrl, buildAgentProfileUrlFromSlug } from "@/lib/url";
import { cn } from "@/lib/utils";
import type { DetailedListingListItem } from "@/lib/types";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

type SerializedBroker = {
  id: string;
  slug: string;
  name: string;
  image: string | null;
  isVerified: boolean;
  organization: string;
  locationLabel: string | null;
  specialties: string[];
  yearsCount: number | null;
  listingsCount: number | null;
};

type StatItem = {
  label: string;
  value: string;
};

type CrossLinkItem = {
  name: string;
  href: string;
};

export type LocalMarketData = {
  stateName: string;
  stateSlug: string;
  businessTypeName?: string;
  businessTypeSlug?: string;
  stats: StatItem[];
  listings: DetailedListingListItem[];
  listingsHeading: string;
  listingsSearchHref: string;
  brokers: SerializedBroker[];
  brokersHeading: string;
  brokersSearchHref: string;
  popularTypes: CrossLinkItem[];
  relatedStates: CrossLinkItem[];
};

function ScrollableRow({
  children,
  label,
  linkHref,
  linkLabel,
}: {
  children: React.ReactNode;
  label: string;
  linkHref: string;
  linkLabel: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  return (
    <div className="-mx-4 flex flex-col gap-2.5 sm:-mx-0 md:gap-3">
      <div className="flex items-center justify-between px-4 sm:px-0">
        <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        <Link
          href={linkHref}
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {linkLabel}
        </Link>
      </div>

      <Carousel
        opts={{ align: "start", dragFree: true }}
        setApi={setApi}
        className="w-full"
      >
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 left-0 z-[5] w-4 bg-gradient-to-r from-card/80 to-transparent transition-opacity sm:w-6",
            canPrev ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 right-0 z-[5] w-6 bg-gradient-to-l from-card/80 via-card/60 to-transparent transition-opacity sm:w-10",
            canNext ? "opacity-100" : "opacity-0",
          )}
        />

        {children}

        {canPrev && (
          <button
            type="button"
            aria-label="Previous"
            className="absolute left-1 top-1/2 z-20 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card/95 text-foreground shadow-sm transition-colors hover:bg-card md:inline-flex"
            onClick={() => api?.scrollPrev()}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        )}
        {canNext && (
          <button
            type="button"
            aria-label="Next"
            className="absolute right-1 top-1/2 z-20 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card/95 text-foreground shadow-sm transition-colors hover:bg-card md:inline-flex"
            onClick={() => api?.scrollNext()}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}
      </Carousel>
    </div>
  );
}

export function LocalMarketContent({ data }: { data: LocalMarketData }) {
  const hasListings = data.listings.length > 0;
  const hasBrokers = data.brokers.length > 0;
  const hasStats = data.stats.length > 0;
  const hasTypes = data.popularTypes.length > 0;
  const hasStates = data.relatedStates.length > 0;

  if (!hasListings && !hasBrokers && !hasStats) return null;

  const sectionTitle = data.businessTypeName
    ? `The ${data.businessTypeName} market in ${data.stateName}`
    : `The ${data.stateName} business market`;

  const sectionDescription = data.businessTypeName
    ? `See what the ${data.businessTypeName.toLowerCase()} landscape looks like in ${data.stateName} — active listings, local brokers, and key market data.`
    : `Explore the business-for-sale landscape in ${data.stateName} — active listings, verified brokers, and market data to inform your valuation.`;

  return (
    <section className="mt-4 overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-card via-theme-blue-soft to-[theme-green-soft] px-4 py-5 sm:px-5 md:mt-6 md:px-7 md:py-7">
      <div className="flex flex-col gap-5 md:gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-base font-semibold text-foreground sm:text-lg md:text-xl">
            {sectionTitle}
          </h2>
          <p className="text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
            {sectionDescription}
          </p>
        </div>

        {hasStats && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-border/50 pb-4 sm:flex sm:flex-wrap sm:gap-x-6 sm:gap-y-2 sm:pb-5">
            {data.stats.map((stat) => (
              <div key={stat.label} className="flex items-baseline gap-1.5">
                <span className="font-data text-sm font-bold text-foreground sm:text-base">
                  {stat.value}
                </span>
                <span className="text-[11px] text-muted-foreground sm:text-xs">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {hasListings && (
          <ScrollableRow
            label={data.listingsHeading}
            linkHref={data.listingsSearchHref}
            linkLabel="View all →"
          >
            <CarouselContent className="-ml-2.5 items-stretch py-1 pl-4 sm:-ml-3 sm:pl-0">
              {data.listings.map((listing) => (
                <CarouselItem
                  key={listing.id}
                  className="min-w-0 basis-[240px] pl-2.5 sm:basis-[260px] sm:pl-3 md:basis-[300px]"
                >
                  <Link
                    href={buildListingUrl(listing)}
                    className="block h-full w-full"
                  >
                    <ListingPreview
                      listing={listing}
                      noFavoriteButton
                      noSeenOrContactedBadge
                      noAgentBadge
                    />
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
          </ScrollableRow>
        )}

        {hasBrokers && (
          <ScrollableRow
            label={data.brokersHeading}
            linkHref={data.brokersSearchHref}
            linkLabel="Find a broker →"
          >
            <CarouselContent className="-ml-2.5 items-stretch py-1 pl-4 sm:-ml-3 sm:pl-0">
              {data.brokers.map((broker) => (
                <CarouselItem
                  key={broker.id}
                  className="min-w-0 basis-[260px] pl-2.5 sm:basis-[280px] sm:pl-3 md:basis-[300px]"
                >
                  <BrokerProfileCard
                    href={buildAgentProfileUrlFromSlug(broker.slug)}
                    id={broker.id}
                    name={broker.name}
                    image={broker.image}
                    isVerified={broker.isVerified}
                    organization={broker.organization}
                    locationLabel={broker.locationLabel}
                    specialties={broker.specialties}
                    yearsCount={broker.yearsCount}
                    listingsCount={broker.listingsCount}
                    ctaLabel="View profile"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </ScrollableRow>
        )}

        {(hasTypes || hasStates) && (
          <div className="flex flex-col gap-4 border-t border-border/50 pt-4 sm:pt-5">
            {hasTypes && (
              <div>
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:text-xs">
                  {data.businessTypeName
                    ? `Other business types in ${data.stateName}`
                    : `Popular business types in ${data.stateName}`}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {data.popularTypes.map((t) => (
                    <Link
                      key={t.href}
                      href={t.href}
                      className="rounded-full border border-border bg-card/80 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground"
                    >
                      {t.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {hasStates && (
              <div>
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:text-xs">
                  {data.businessTypeName
                    ? `${data.businessTypeName} valuation in other states`
                    : "Business valuation in other states"}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {data.relatedStates.map((s) => (
                    <Link
                      key={s.href}
                      href={s.href}
                      className="rounded-full border border-border bg-card/80 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground"
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
